import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Building2, Dumbbell, ShoppingBag, Rocket, Utensils, User,
  Cloud, Shirt, GraduationCap, Coins, Search, ArrowRight, Sparkles,
  Loader2, Plus, Edit2, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  'building': Building2,
  'dumbbell': Dumbbell,
  'shopping-bag': ShoppingBag,
  'rocket': Rocket,
  'utensils': Utensils,
  'user': User,
  'cloud': Cloud,
  'shirt': Shirt,
  'graduation-cap': GraduationCap,
  'coins': Coins,
  'sparkles': Sparkles,
};

const ICON_OPTIONS = [
  'sparkles', 'building', 'dumbbell', 'shopping-bag', 'rocket', 'utensils',
  'user', 'cloud', 'shirt', 'graduation-cap', 'coins',
];

const CATEGORY_COLORS = {
  'Business': 'bg-blue-100 text-blue-700',
  'Health': 'bg-green-100 text-green-700',
  'Technology': 'bg-purple-100 text-purple-700',
  'Food & Beverage': 'bg-orange-100 text-orange-700',
  'Personal': 'bg-pink-100 text-pink-700',
  'Lifestyle': 'bg-rose-100 text-rose-700',
  'Education': 'bg-cyan-100 text-cyan-700',
  'Finance': 'bg-emerald-100 text-emerald-700',
};

const ALL_PLATFORMS = ['Instagram', 'Facebook', 'LinkedIn', 'Twitter', 'TikTok', 'YouTube', 'Bluesky'];
const TONES = ['Professional', 'Casual', 'Inspirational', 'Humorous', 'Bold', 'Educational'];

const EMPTY_FORM = {
  name: '',
  icon: 'sparkles',
  category: '',
  topic: '',
  audience: '',
  tone: 'Professional',
  platforms: ['LinkedIn'],
  suggested_posts: 10,
  description: '',
  style_profile_id: '',
};

export const TemplatesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [styleProfiles, setStyleProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editing, setEditing] = useState(null); // null = closed; {} = new; {...} = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpl, sp] = await Promise.all([
        api.get('/templates'),
        api.get('/style-profiles'),
      ]);
      setTemplates(tpl.data?.templates || []);
      setStyleProfiles(sp.data?.profiles || []);
    } catch (_) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const categories = ['all', ...Array.from(new Set(templates.map((t) => t.category)))];

  const filteredTemplates = templates.filter((template) => {
    const desc = template.description || '';
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      desc.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleUseTemplate = (template) => {
    // Stash the linked Style Profile (if any) in sessionStorage so the
    // Generator page can pick it up and pre-populate the "Add more detail"
    // section. URL params can't safely carry 4KB of style_example text.
    if (template.style_profile_id) {
      const profile = styleProfiles.find((p) => p.id === template.style_profile_id);
      if (profile) {
        try {
          sessionStorage.setItem('pending_style_profile', JSON.stringify(profile));
        } catch (_) { /* quota — ignore */ }
      }
    } else {
      sessionStorage.removeItem('pending_style_profile');
    }
    const params = new URLSearchParams({
      template: template.id,
      topic: template.topic,
      audience: template.audience,
      tone: template.tone,
      platforms: template.platforms.join(','),
      posts: template.suggested_posts.toString(),
    });
    navigate(`/generate?${params.toString()}`);
    toast.success(`Using "${template.name}" template`);
  };

  const openCreate = () => { setEditing({}); setForm(EMPTY_FORM); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      icon: t.icon || 'sparkles',
      category: t.category || '',
      topic: t.topic || '',
      audience: t.audience || '',
      tone: t.tone || 'Professional',
      platforms: t.platforms || ['LinkedIn'],
      suggested_posts: t.suggested_posts || 10,
      description: t.description || '',
      style_profile_id: t.style_profile_id || '',
    });
  };

  const togglePlatform = (p) => {
    setForm((f) =>
      f.platforms.includes(p)
        ? { ...f, platforms: f.platforms.filter((x) => x !== p) }
        : { ...f, platforms: [...f.platforms, p] }
    );
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.category.trim()) { toast.error('Category is required'); return; }
    if (!form.topic.trim() || !form.audience.trim()) { toast.error('Topic and audience are required'); return; }
    if (form.platforms.length === 0) { toast.error('Pick at least one platform'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      icon: form.icon || 'sparkles',
      category: form.category.trim(),
      topic: form.topic.trim(),
      audience: form.audience.trim(),
      tone: form.tone,
      platforms: form.platforms,
      suggested_posts: form.suggested_posts,
      description: form.description.trim() || null,
      style_profile_id: form.style_profile_id || null,
    };
    try {
      if (editing?.id && editing.source === 'user') {
        await api.put(`/templates/${editing.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/templates', payload);
        toast.success('Template created');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Content Templates</h1>
          <p className="text-zinc-500">Quick-start templates with optional Style Profile auto-apply.</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Create template
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="template-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((category) => (
            <Button
              key={category}
              size="sm"
              variant={selectedCategory === category ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            >
              {category === 'all' ? 'All' : category}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card className="border-zinc-200">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">No templates found</p>
            <p className="text-zinc-400 text-sm mt-1">Try adjusting your search or create one</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => {
            const IconComponent = ICON_MAP[template.icon] || Sparkles;
            const isUser = template.source === 'user';
            const linkedProfile = template.style_profile_id
              ? styleProfiles.find((p) => p.id === template.style_profile_id)
              : null;
            return (
              <Card
                key={template.id}
                className="border-zinc-200 hover:border-indigo-200 hover:shadow-md transition-all group"
                data-testid={`template-${template.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-100 transition-colors">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1">
                      {isUser && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openEdit(template); }}
                            data-testid={`edit-template-${template.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={(e) => { e.stopPropagation(); handleDelete(template); }}
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      <Badge className={CATEGORY_COLORS[template.category] || 'bg-zinc-100 text-zinc-700'}>
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-3 group-hover:text-indigo-600 transition-colors flex items-center gap-2">
                    <span className="truncate">{template.name}</span>
                    {isUser && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        Mine
                      </Badge>
                    )}
                  </CardTitle>
                  {template.description && <CardDescription>{template.description}</CardDescription>}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-1">
                      {template.platforms.map((platform) => (
                        <span
                          key={platform}
                          className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                    {linkedProfile && (
                      <div
                        className="flex items-center gap-1 text-[11px] text-indigo-700"
                        data-testid={`template-style-profile-${template.id}`}
                      >
                        <Sparkles className="w-3 h-3" />
                        <span className="truncate">Style: {linkedProfile.name}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
                      <span className="text-sm text-zinc-500">
                        {template.suggested_posts} posts suggested
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 group-hover:bg-indigo-50"
                        onClick={() => handleUseTemplate(template)}
                        data-testid={`use-template-${template.id}`}
                      >
                        Use Template
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit template' : 'Create template'}</DialogTitle>
            <DialogDescription>
              Save reusable presets that pre-fill the AI generator. Link a Style Profile to also load voice + extras automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="tpl-name">Name *</Label>
                <Input
                  id="tpl-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. LinkedIn Educational Thread"
                  data-testid="template-name-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="tpl-category">Category *</Label>
                <Input
                  id="tpl-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g. Education, Business…"
                  data-testid="template-category-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="tpl-desc">Description</Label>
              <Input
                id="tpl-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="One-line summary"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tone</Label>
                <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Icon</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ALL_PLATFORMS.map((p) => (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant={form.platforms.includes(p) ? 'default' : 'outline'}
                    onClick={() => togglePlatform(p)}
                    className={`text-xs h-7 ${form.platforms.includes(p) ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="tpl-suggested">Suggested posts</Label>
                <Input
                  id="tpl-suggested"
                  type="number"
                  min={1}
                  max={365}
                  value={form.suggested_posts}
                  onChange={(e) => setForm({ ...form, suggested_posts: Number.parseInt(e.target.value, 10) || 1 })}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                  Style profile (optional)
                </Label>
                <Select
                  value={form.style_profile_id || '__none__'}
                  onValueChange={(v) => setForm({ ...form, style_profile_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger data-testid="template-style-profile-select">
                    <SelectValue placeholder="— None —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {styleProfiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="tpl-topic">Topic *</Label>
              <Textarea
                id="tpl-topic"
                rows={2}
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                placeholder="What is the post about? e.g. Educational LinkedIn content about AI tools for marketers."
              />
            </div>
            <div>
              <Label htmlFor="tpl-audience">Target audience *</Label>
              <Textarea
                id="tpl-audience"
                rows={2}
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                placeholder="Who is this for? e.g. SMB founders, agency owners, B2B marketers…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="template-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editing?.id ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
